import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuthToken } from "../../utils/getAuthToken";
import { checkSubscriptionActive } from "../../utils/subscriptionError";
import { toast } from "react-hot-toast";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from '@mui/icons-material/Edit';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import { fetchUserDetails, fetchUserPermissions } from "../../redux/Slice/User/userDetailsSlice";
import { setSelectedWorkflow } from '../../redux/Slice/Workflow/workflowSlice';
import { fetchWorkspaces, switchWorkspace, setCurrentWorkspace } from "../../redux/Slice/Workspace/workspaceSlice";
import { selectAccount } from "../../redux/selectors";
import axios from 'axios';
import EditWorkflowNameDialog from '../Workflow/EditWorkflowNameDialog';
import { getConnectionStatus, onConnectionChange, onDisconnectionChange } from "../../utils/socketHandler";
import connectionMonitor from "../../utils/connectionMonitor";

/** Max characters for workflow title in TopNav on xs (full title on sm+). */
const XS_WORKFLOW_NAV_TITLE_MAX_CHARS = 26;

function truncateWorkflowNavTitleForXs(title, maxLen = XS_WORKFLOW_NAV_TITLE_MAX_CHARS) {
  if (typeof title !== "string" || !title) return title;
  const t = title.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(1, maxLen - 1)).trimEnd()}…`;
}

// Create a styled component for the main AppBar
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  boxShadow: theme.palette.mode === "dark"
    ? "0 1px 2px rgba(0, 0, 0, 0.3)"
    : "0 1px 2px rgba(0, 0, 0, 0.08)",
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: "static",
  height: { xs: "40px", sm: "48px", md: "50px", lg: "50px", xl: "50px" },
  color: theme.palette.text.primary,
  "& .MuiAppBar-root": {
    backgroundColor: theme.palette.background.default,
  },
}));

// Page title component with clean typography
const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: { xs: "10px", sm: "13px", md: "15px", lg: "15px", xl: "15px" },
  lineHeight: { xs: "16px", sm: "20px", md: "20px", lg: "20px", xl: "20px" },
  marginTop: { xs: "8px", sm: "12px", md: "16px", lg: "16px", xl: "16px" },
  marginBottom: { xs: "0px", sm: "0px", md: "0px", lg: "0px", xl: "0px" },
  letterSpacing: "-0.23px",
  color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
  fontFamily: "'Instrument Sans', 'Figtree', 'Roboto', sans-serif",
  marginRight: "auto",
}));

const TopNav = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  // Get page information from Redux store
  const { currentPage, breadcrumbs } = useSelector((state) => state.leftNav);
  const { selectedWorkflow } = useSelector((state) => state.workflow);
  const { selectedOption } = useSelector((state) => state.settings);
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const workspaces = useSelector((state) => state.workspace?.workspaces) || [];
  const account = useSelector(selectAccount);
  const workspaceLabel = currentWorkspace?.name || account?.Name;
  const canSwitchWorkspace = useSelector(
    (state) => state.userDetails?.userPermissions?.permissions?.Workspace?.View === true
  );

  // Workspace switcher dropdown (reuses the same workspace list/switch thunks as
  // the LeftNav profile menu's WorkspaceSwitcher — same backend, no new API).
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
  const workspaceIndex = workspaces.findIndex((w) => w._id === currentWorkspace?._id);
  const workspaceCountLabel =
    workspaces.length > 1 ? `Workspace ${workspaceIndex + 1} of ${workspaces.length}` : null;

  useEffect(() => {
    if (canSwitchWorkspace) {
      dispatch(fetchWorkspaces());
    }
  }, [dispatch, canSwitchWorkspace]);

  const handleWorkspaceMenuOpen = (event) => {
    if (!canSwitchWorkspace) return;
    setWorkspaceMenuAnchor(event.currentTarget);
  };
  const handleWorkspaceMenuClose = () => setWorkspaceMenuAnchor(null);

  const handleSwitchWorkspace = async (workspace) => {
    if (workspace._id === currentWorkspace?._id) {
      handleWorkspaceMenuClose();
      return;
    }
    try {
      await dispatch(switchWorkspace(workspace._id)).unwrap();
      dispatch(setCurrentWorkspace(workspace));
      handleWorkspaceMenuClose();
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to switch workspace");
    }
  };

  // Helper function to get short option names for xs screens
  const getShortOptionName = (option) => {
    if (!isXs) return option;
    const nameMap = {
      "Team Members": "Members",
      "Roles & Permission": "Permissions",
      "Notification": "Notifications",
    };
    return nameMap[option] || option;
  };

  // State for workflow name edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [, setEditName] = useState("");
  const [, setEditDesc] = useState("");

  // Fetch user details and permissions on component mount
  useEffect(() => {
    dispatch(fetchUserDetails());
    dispatch(fetchUserPermissions());
  }, [dispatch]);

  // Own-connection presence ring on the avatar (green connected / orange away)
  // — subscribes to connectionMonitor's own connect/disconnect events
  // directly (same underlying singleton App.js already subscribes to for its
  // own connectionStatus state), kept local here to avoid prop-drilling.
  const [, setIsSocketConnected] = useState(() => getConnectionStatus().isConnected);
  useEffect(() => {
    const handleConnected = () => setIsSocketConnected(true);
    const handleDisconnected = () => setIsSocketConnected(false);
    onConnectionChange(handleConnected);
    onDisconnectionChange(handleDisconnected);
    setIsSocketConnected(getConnectionStatus().isConnected);
    return () => {
      connectionMonitor.off('connection', handleConnected);
      connectionMonitor.off('disconnection', handleDisconnected);
    };
  }, []);

  // Detect if on workflow create page
  const isWorkflowCreatePage = /^\/home\/[^/]+\/workflow\/[^/]+\/create/.test(location.pathname);
  /** Tighter Workflow breadcrumb row on xs: less gap, aligned chevron, pencil closer to title */
  const workflowNavTightXs =
    isXs &&
    Array.isArray(breadcrumbs) &&
    breadcrumbs.length > 1 &&
    breadcrumbs[0] === "Workflow";

  // Save handler for workflow name/desc

  return (
    <StyledAppBar>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "center", sm: "center", md: "center" },
          minHeight: { xs: "40px", sm: "48px", md: "50px", lg: "50px", xl: "50px" },
          padding: { xs: "4px 8px 4px 8px", sm: "8px 16px 4px 16px", md: "8px 24px 4px 24px", lg: "8px 24px 4px 24px", xl: "8px 24px 4px 24px" },
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        {/* Left Section - Page Title or Breadcrumbs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            pr: workflowNavTightXs ? 0.5 : 0,
          }}
        >
          {breadcrumbs && breadcrumbs.length > 1 ? (
            <Breadcrumbs
              separator={
                <NavigateNextIcon
                  fontSize="small"
                  sx={{
                    color: "#44B57C",
                    position: "relative",
                    ...(workflowNavTightXs
                      ? {
                          bottom: 0,
                          top: 0,
                          display: "block",
                          fontSize: "1.125rem",
                          /** Optical center with 12–16px crumb text (xs workflow row) */
                          transform: "translateY(2px)",
                        }
                      : {
                          bottom: "-10px",
                        }),
                  }}
                />
              }
              aria-label="breadcrumb"
              sx={{
                "& .MuiBreadcrumbs-ol": {
                  alignItems: "center",
                  flexWrap: { xs: "nowrap", sm: "wrap" },
                  ...(workflowNavTightXs
                    ? {
                        pl: 0,
                        ml: 0,
                        alignItems: "center",
                      }
                    : {}),
                },
                ...(workflowNavTightXs
                  ? {
                      "& .MuiBreadcrumbs-separator": {
                        mx: "2px",
                        my: 0,
                        display: "flex",
                        alignItems: "center",
                        alignSelf: "center",
                        justifyContent: "center",
                        height: "100%",
                      },
                      "& .MuiBreadcrumbs-li": {
                        py: 0,
                        display: "flex",
                        alignItems: "center",
                      },
                    }
                  : {}),
              }}
            >
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                const isWorkflowContext = breadcrumbs[0] === "Workflow";
                const shortenWorkflowTitleXs =
                  isXs && isWorkflowContext && isLast && typeof crumb === "string";
                const displayCrumb = shortenWorkflowTitleXs
                  ? truncateWorkflowNavTitleForXs(crumb)
                  : crumb;
                // If this is the workflow name (second breadcrumb, after 'Workflow'), make it unbold and smaller
                const isWorkflowName = breadcrumbs[0] === "Workflow" && index === 1;
                const fontSize = isLast
                  ? (isWorkflowName ? { xs: "12px", sm: "16px", md: "18px" } : { xs: "16px", sm: "24px", md: "30px" })
                  : { xs: `${16 - (breadcrumbs.length - index) * 1}px`, sm: `${24 - (breadcrumbs.length - index) * 1.5}px`, md: `${30 - (breadcrumbs.length - index) * 2}px` };
                const fontWeight = isWorkflowName ? 400 : 700;
                return isLast ? (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                      maxWidth: shortenWorkflowTitleXs
                        ? { xs: "min(56vw, 200px)", sm: "none" }
                        : undefined,
                      ml: workflowNavTightXs ? -0.25 : 0,
                    }}
                  >
                  <Typography
                    fontWeight={fontWeight}
                    fontSize={fontSize}
                    lineHeight="20px"
                    letterSpacing="-0.23px"
                    color="#44B57C"
                    fontFamily="'Instrument Sans', 'Figtree', 'Roboto', sans-serif"
                    marginTop={{
                      xs: workflowNavTightXs ? 0 : "8px",
                      sm: "12px",
                      md: "16px",
                      lg: "16px",
                      xl: "16px",
                    }}
                    marginBottom="0px"
                    sx={
                      shortenWorkflowTitleXs
                        ? {
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                          }
                        : undefined
                    }
                  >
                    {displayCrumb}
                  </Typography>
                    {/* Pencil icon for workflow name breadcrumb on workflow create page */}
                    {isWorkflowCreatePage && isWorkflowName && (
                      <IconButton
                        size="small"
                        sx={{
                          ml: workflowNavTightXs ? 0.125 : 1,
                          mr: workflowNavTightXs ? 0.25 : 0,
                          mt: {
                            xs: workflowNavTightXs ? 0 : "8px",
                            sm: "12px",
                            md: "16px",
                            lg: "16px",
                            xl: "16px",
                          },
                          p: workflowNavTightXs ? 0.25 : undefined,
                          color: "#44B57C",
                          flexShrink: 0,
                        }}
                        onClick={() => {
                          setEditName(crumb);
                          setEditDesc("");
                          setEditDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Link
                    key={index}
                    underline="hover"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                  fontWeight: fontWeight,
                  fontSize: fontSize,
                  lineHeight: "20px",
                  letterSpacing: "-0.23px",
                  color: "#44B57C",
                  fontFamily:
                    "'Instrument Sans', 'Figtree', 'Roboto', sans-serif",
                  marginTop: {
                    xs: workflowNavTightXs ? 0 : "8px",
                    sm: "12px",
                    md: "16px",
                    lg: "16px",
                    xl: "16px",
                  },
                  marginBottom: "0px",
                  opacity: 0.8,
                  mr: workflowNavTightXs ? 0 : undefined,
                    }}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // Navigate to the appropriate page
                      if (crumb === "Templates") {
                        navigate(
                          `/home/${location.pathname.split("/")[2]}/templates`
                        );
                      }
                    }}
                  >
                    {crumb}
                  </Link>
                );
              })}
            </Breadcrumbs>
          ) : currentPage === "Settings" ? (
            // Display Settings with selected option
            <Breadcrumbs
              separator={
                <NavigateNextIcon
                  fontSize="small"
                  sx={{
                    color: "#44B57C",
                    fontSize: { xs: "10px", sm: "small", md: "18px", lg: "20px", xl: "22px" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              }
              aria-label="breadcrumb"
              sx={{
                "& .MuiBreadcrumbs-ol": {
                  alignItems: "center",
                  flexWrap: { xs: "nowrap", sm: "wrap" },
                  display: "flex",
                  flexDirection: "row",
                },
                "& .MuiBreadcrumbs-li": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                "& .MuiBreadcrumbs-separator": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: { xs: "0 4px", sm: "0 6px", md: "0 8px" },
                },
              }}
            >
              <Link
                underline="hover"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "700",
                  fontSize: { xs: "16px", sm: "22px", md: "28px", lg: "28px", xl: "28px" },
                  lineHeight: "1.2",
                  letterSpacing: "-0.23px",
                  color: "#44B57C",
                  fontFamily:
                    "'Instrument Sans', 'Figtree', 'Roboto', sans-serif",
                  margin: "0px",
                  padding: "0px",
                  opacity: 0.8,
                  whiteSpace: "nowrap",
                  height: "100%",
                }}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  // Do nothing on main settings click as we're already in settings
                }}
              >
                Settings
              </Link>
              <Typography
                fontWeight="700"
                fontSize={{ xs: "16px", sm: "24px", md: "30px", lg: "30px", xl: "30px" }}
                lineHeight="1.2"
                letterSpacing="-0.23px"
                color="#44B57C"
                fontFamily="'Instrument Sans', 'Figtree', 'Roboto', sans-serif"
                margin="0px"
                padding="0px"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: "120px", sm: "none" },
                  height: "100%",
                }}
              >
                {getShortOptionName(selectedOption)}
              </Typography>
            </Breadcrumbs>
          ) : (
            <PageTitle variant="h1" component="h1">
              {currentPage}
            </PageTitle>
          )}
        </Box>

        {/* Right Section - Current Workspace (moved out of the Inbox panel, now global; opens the real workspace switcher).
            The AI Agents page's "Learn More" button briefly lived here
            (Rahim, 2026-09-01) but TopNav is now hidden entirely on /ai
            routes (Home.jsx's NO_TOP_NAV_ROUTES) — that button moved to
            AiSettings.jsx's own PanelHeading instead, same pattern Inbox
            uses. See [[ai-settings-page-rebuild-plan]]. */}
        {workspaceLabel && (
          <>
            <Box
              onClick={handleWorkspaceMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                gap: { xs: 0.5, sm: 0.75 },
                maxWidth: { xs: "96px", sm: "320px", md: "380px" },
                color: (theme) => theme.palette.text.secondary,
                py: 0.5,
                px: 1,
                borderRadius: "8px",
                cursor: canSwitchWorkspace ? "pointer" : "default",
                transition: "background-color 0.15s ease",
                "&:hover": canSwitchWorkspace
                  ? { backgroundColor: (theme) => theme.palette.action.hover }
                  : undefined,
              }}
            >
              <WorkspacesOutlinedIcon
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.125rem" }, flexShrink: 0, color: "text.secondary" }}
              />
              {!isXs && (
                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.15 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { sm: "0.75rem", md: "0.8125rem", lg: "0.875rem" },
                      color: "text.primary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {workspaceLabel}
                  </Typography>
                  {workspaceCountLabel && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { sm: "0.6875rem", md: "0.75rem" },
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {workspaceCountLabel}
                    </Typography>
                  )}
                </Box>
              )}
              {!isXs && canSwitchWorkspace && (
                <ArrowDropDownIcon
                  sx={{ fontSize: "1.25rem", flexShrink: 0, ml: -0.25, alignSelf: "flex-end", mb: "-2px" }}
                />
              )}
            </Box>

            <Menu
              anchorEl={workspaceMenuAnchor}
              open={Boolean(workspaceMenuAnchor)}
              onClose={handleWorkspaceMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { minWidth: 240, mt: 1 } }}
            >
              {workspaces.length === 0 ? (
                <MenuItem disabled>No workspaces available</MenuItem>
              ) : (
                workspaces.map((workspace) => (
                  <MenuItem
                    key={workspace._id}
                    onClick={() => handleSwitchWorkspace(workspace)}
                    sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {workspace.name}
                    </Typography>
                    {currentWorkspace?._id === workspace._id && (
                      <CheckIcon sx={{ fontSize: 18, color: "primary.main" }} />
                    )}
                  </MenuItem>
                ))
              )}
            </Menu>
          </>
        )}
      </Toolbar>

      {/* Workflow name edit dialog */}
      <EditWorkflowNameDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        name={selectedWorkflow?.currentVersion?.name || ''}
        description={selectedWorkflow?.currentVersion?.description || ''}
        onSave={async (newName, newDesc) => {
          const { blocked, message: blockMsg } = await checkSubscriptionActive(getAuthToken);
          if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
          if (!selectedWorkflow || !selectedWorkflow.currentVersion) {
            setEditDialogOpen(false);
            return;
          }
          const updatedWorkflow = {
            ...selectedWorkflow,
            currentVersion: {
              ...selectedWorkflow.currentVersion,
              name: newName,
              description: newDesc,
            },
          };
          dispatch(setSelectedWorkflow(updatedWorkflow));
          try {
            // Get token from memory (not localStorage)
            const token = await getAuthToken();
            if (!token) {
              console.error("No token found");
              return;
            }
            const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
            await axios.put(
              `${serverDomain}/api/workflow/update/${selectedWorkflow._id}`,
              updatedWorkflow,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          } catch (err) {
            console.error('Failed to update workflow name/desc', err);
          }
          setEditDialogOpen(false);
        }}
      />
    </StyledAppBar>
  );
};

export default TopNav;